import React, { FC, useState } from 'react';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppUserRating } from '../models/appUserRatingsModels';
import { Table } from 'shared/components';

interface Props {
  ratings: AppUserRating[];
  totalItems: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  handlePageChange: (currentPage: number) => void;
}

const StarRating: React.FC<{ score: number }> = ({ score }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <FontAwesomeIcon
        key={star}
        icon={faStar}
        style={{ color: star <= score ? '#f59e0b' : '#d1d5db', fontSize: '14px' }}
      />
    ))}
  </div>
);

const STATE_STYLES: Record<string, { bg: string; color: string }> = {
  'Viaje Finalizado': { bg: '#f0fdf4', color: 'rgb(0, 112, 56)' },
  'Auditoria pendiente': { bg: '#fffbeb', color: '#b45309' }
};

const getStateStyle = (state: string) =>
  STATE_STYLES[state] ?? { bg: '#f3f4f6', color: '#374151' };

const AppUserRatingsTable: FC<Props> = ({
  ratings,
  totalItems,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  handlePageChange
}) => {
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());

  const toggleComment = (index: number) =>
    setExpandedComments((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const columns = [
    {
      name: 'reservationId',
      label: 'Reserva #',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => (
          <span className="font-semibold text-blue-700 whitespace-nowrap">
            #{ratings[rowIndex].reservation.id}
          </span>
        )
      }
    },
    {
      name: 'date',
      label: 'Fecha y Hora',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (_: any, { rowIndex }: any) => {
          const dt = new Date(ratings[rowIndex].reservation.reservation_datetime);
          const fecha = dt.toLocaleDateString('es-AR');
          const hora = dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
          return (
            <div className="flex gap-3">
              <span className="text-blue-800 whitespace-nowrap">{fecha}</span>
              <span className="text-blue-500 whitespace-nowrap">{hora}</span>
            </div>
          );
        }
      }
    },
    {
      name: 'serviceType',
      label: 'Tipo de Servicio',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => (
          <span className="whitespace-nowrap text-gray-700">
            {ratings[rowIndex].reservation.service_type}
          </span>
        )
      }
    },
    {
      name: 'clientName',
      label: 'Cliente',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => (
          <span className="whitespace-nowrap">{ratings[rowIndex].client?.name?.trim() || 'N/A'}</span>
        )
      }
    },
    {
      name: 'driverName',
      label: 'Chofer',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => {
          const driver = ratings[rowIndex].driver;
          return (
            <div>
              <div className="font-medium whitespace-nowrap">{driver?.name || 'N/A'}</div>
              {driver?.code != null && (
                <div className="text-xs font-semibold text-blue-600 whitespace-nowrap">#{driver.code}</div>
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'score',
      label: 'Calificación',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (_: any, { rowIndex }: any) => {
          const score = ratings[rowIndex].rating.score;
          return (
            <div className="flex items-center gap-2">
              <StarRating score={score} />
              <span className="font-bold text-gray-500 text-sm">{score}/5</span>
            </div>
          );
        }
      }
    },
    {
      name: 'comment',
      label: 'Comentario',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => {
          const comment = ratings[rowIndex].rating.comment;
          if (!comment) return <span className="text-gray-400 text-xs">Sin comentario</span>;
          const expanded = expandedComments.has(rowIndex);
          return (
            <div
              style={{ maxWidth: '300px', cursor: 'pointer' }}
              onClick={() => toggleComment(rowIndex)}
              title={expanded ? 'Click para colapsar' : 'Click para expandir'}
            >
              <span
                className="italic text-gray-600"
                style={
                  expanded
                    ? { display: 'inline-block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
                    : { display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '280px' }
                }
              >
                &ldquo;{comment}&rdquo;
              </span>
              <span className="text-xs text-blue-400 ml-1">{expanded ? '▲' : '▼'}</span>
            </div>
          );
        }
      }
    },
    {
      name: 'state',
      label: 'Estado',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_: any, { rowIndex }: any) => {
          const state = ratings[rowIndex].reservation.state;
          const style = getStateStyle(state);
          return (
            <span
              style={{
                backgroundColor: style.bg,
                color: style.color,
                borderRadius: '0.375rem',
                padding: '2px 10px',
                fontWeight: 'bold',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}
            >
              {state}
            </span>
          );
        }
      }
    }
  ];

  const data = ratings.map((r) => ({
    reservationId: r.reservation.id,
    date: r.reservation.reservation_datetime,
    serviceType: r.reservation.service_type,
    state: r.reservation.state,
    clientName: r.client?.name ?? 'N/A',
    driverName: r.driver?.name ?? 'N/A',
    score: r.rating.score,
    comment: r.rating.comment
  }));

  return (
    <div className="w-full">
      <Table
        title=""
        options={{
          filter: false,
          search: false,
          download: false,
          viewColumns: false,
          elevation: 0,
          selectableRows: 'none',
          print: false,
          serverSide: true,
          count: totalItems,
          page: page - 1,
          rowsPerPage,
          onChangePage(page: number) {
            onChangePage(page + 1, rowsPerPage);
            handlePageChange(page + 1);
          },
          onChangeRowsPerPage(rpp: number) {
            onChangeRowsPerPage(rpp);
          },
          onColumnSortChange: (changedColumn: string, direction: 'asc' | 'desc') => {
            onSortChange(direction, changedColumn);
          }
        }}
        data={data}
        columns={columns}
      />
    </div>
  );
};

export default AppUserRatingsTable;
